import { removeNamespace } from '@/utils/helpers'
import { NotificationType, NotificationItemSearchType, NotificationState } from '@/utils/enums/notification-enums'
import * as cloneDeep from 'lodash.clonedeep'
import NotificationService from '@/services/notification.service'

export const NOTIFICATIONS_TYPES = {
  mutations: {
    SET_LOADING_NOTIFICATIONS: 'notifications/SET_LOADING_NOTIFICATIONS',
    SET_PAGED_NOTIFICATIONS: 'notifications/SET_PAGED_NOTIFICATIONS',
    SET_TOTAL_NOTIFICATIONS_COUNT: 'notifications/SET_TOTAL_NOTIFICATIONS_COUNT',
    SET_ALL_TOTAL_NOTIFICATIONS_COUNT: 'notifications/SET_ALL_TOTAL_NOTIFICATIONS_COUNT',
    SET_ITEM_SEARCH_OPTIONS: 'notifications/SET_ITEM_SEARCH_OPTIONS',
    SET_NOTIFICATION_ITEM_SEARCH_TYPE: 'notifications/SET_NOTIFICATION_ITEM_SEARCH_TYPE',
    SET_NOTIFICATIONS_PAGED_OPTIONS: 'notifications/SET_NOTIFICATIONS_PAGED_OPTIONS',
    UPDATE_NOTIFICATION: 'notifications/UPDATE_NOTIFICATION'
  },
  actions: {
    fetchNotificationMetrics: 'notifications/fetchNotificationMetrics',
    fetchNotificationsPaged: 'notifications/fetchNotificationsPaged',
    updateNotification: 'notifications/updateNotification',
    changeItemSearchType: 'notifications/changeItemSearchType',
    dismissAll: 'notifications/dismissAll'
  }
}

const _types = removeNamespace('notifications', NOTIFICATIONS_TYPES)

const state = () => {
  let notificationsDict = {}
  for (var t in NotificationType) {
    const type = NotificationType[t]
    notificationsDict[type] = {}
    for (var s in NotificationState) {
      const state = NotificationState[s]
      notificationsDict[type][state] = {}
      notificationsDict[type][state].loading = false
      notificationsDict[type][state].items = []
      notificationsDict[type][state].totalCount = 0
      notificationsDict[type][state].pagedOptions = {
        itemsPerPage: 10,
        page: 1
      }
    }
  }
  return ({
    notificationsDict: notificationsDict,
    itemSearchOptions: {
      keyword: '',
      searchType: NotificationItemSearchType.Title
    }
  })
}

const getters = {}

const mutations = {
  [_types.mutations.SET_LOADING_NOTIFICATIONS] (state, { dataType, dataState, val }) {
    state.notificationsDict[dataType][dataState].loading = val
  },
  [_types.mutations.SET_PAGED_NOTIFICATIONS] (state, { dataType, dataState, items }) {
    state.notificationsDict[dataType][dataState].items = items
  },
  [_types.mutations.SET_TOTAL_NOTIFICATIONS_COUNT] (state, { dataType, dataState, totalCount }) {
    state.notificationsDict[dataType][dataState].totalCount = totalCount
  },
  [_types.mutations.SET_ALL_TOTAL_NOTIFICATIONS_COUNT] (state, { countByTypeAndState }) {
    for (let stringType in countByTypeAndState) {
      const numericType = NotificationType[stringType]
      for (let stringState in countByTypeAndState[stringType]) {
        const numericState = NotificationState[stringState]
        state.notificationsDict[numericType][numericState].totalCount = countByTypeAndState[stringType][stringState]
      }
    }
  },
  [_types.mutations.SET_NOTIFICATIONS_PAGED_OPTIONS] (state, { dataType, dataState, options }) {
    state.notificationsDict[dataType][dataState].pagedOptions = options
  },
  [_types.mutations.SET_ITEM_SEARCH_OPTIONS] (state, options) {
    state.itemSearchOptions = cloneDeep(options)
  },
  [_types.mutations.SET_NOTIFICATION_ITEM_SEARCH_TYPE] (state, type) {
    state.itemSearchOptions.searchType = type
  },
  [_types.mutations.UPDATE_NOTIFICATION] (state, notification) {
    state.notificationsDict[notification.type][notification.prevState] = state.notificationsDict[notification.type][notification.prevState]
      .items
      .filter(i => i.id !== notification.id)
    state.notificationsDict[notification.type][notification.prevState].totalCount = state.notificationsDict[notification.type][notification.prevState].totalCount--
    state.notificationsDict[notification.type][notification.state].push(notification)
    state.notificationsDict[notification.type][notification.state].totalCount = state.notificationsDict[notification.type][notification.state].totalCount++
  }
}

const actions = {
  async [_types.actions.fetchNotificationMetrics] ({ state, commit }) {
    const { countByTypeAndState } = await NotificationService.getMetrics(state.itemSearchOptions)
    commit(_types.mutations.SET_ALL_TOTAL_NOTIFICATIONS_COUNT, { countByTypeAndState })
  },
  async [_types.actions.fetchNotificationsPaged] ({ state, commit }, { dataType, dataState }) {
    const pagedOptions = state.notificationsDict[dataType][dataState].pagedOptions
    const loadingCommit = _types.mutations.SET_LOADING_NOTIFICATIONS
    const params = {
      type: dataType,
      state: dataState,
      maxResultCount: pagedOptions.itemsPerPage,
      skipCount: (pagedOptions.page - 1) * pagedOptions.itemsPerPage
    }
    commit(loadingCommit, { dataType, dataState, val: true })
    if (dataType === NotificationType.Item) {
      params.keyword = state.itemSearchOptions.keyword
      params.searchType = state.itemSearchOptions.searchType
    }
    try {
      const { items, totalCount } = await NotificationService.getAll(params)
      commit(_types.mutations.SET_PAGED_NOTIFICATIONS, { dataType, dataState, items })
      commit(_types.mutations.SET_TOTAL_NOTIFICATIONS_COUNT, { dataType, dataState, totalCount })
    } finally {
      commit(loadingCommit, { dataType, dataState, val: false })
    }
  },
  async [_types.actions.updateNotification] ({ commit, dispatch }, payload) {
    const loadingCommit = _types.mutations.SET_LOADING_NOTIFICATIONS
    const dataType = payload.type
    const dataState = payload.prevState
    commit(loadingCommit, { dataType, dataState, val: true })
    try {
      await NotificationService.updateNotification(payload)
      await dispatch(_types.actions.fetchNotificationsPaged, { dataType, dataState })
      await dispatch(_types.actions.fetchNotificationMetrics)
    } finally {
      commit(loadingCommit, { dataType, dataState, val: false })
    }
  },
  [_types.actions.changeItemSearchType] ({ commit }, searchType) {
    commit(_types.mutations.SET_NOTIFICATION_ITEM_SEARCH_TYPE, searchType)
  },
  async [_types.actions.dismissAll] ({ commit, state, dispatch }, { dataType, dataState }) {
    const loadingCommit = _types.mutations.SET_LOADING_NOTIFICATIONS
    commit(loadingCommit, { dataType, dataState, val: true })
    try {
      await NotificationService.dismissAll(state.notificationsDict[dataType][dataState].items.map(n => n.id), dataType)
      await dispatch(_types.actions.fetchNotificationsPaged, { dataType, dataState })
      await dispatch(_types.actions.fetchNotificationMetrics)
    } finally {
      commit(loadingCommit, { dataType, dataState, val: false })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
